import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Armchair, Plus, Printer, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ymd } from "@/components/bookings/calendarUtils";

const STATUSES = ["confirmed", "seated", "completed", "cancelled", "no_show"];
const statusColor = { confirmed: "bg-blue-100 text-blue-800", seated: "bg-amber-100 text-amber-800", completed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-700", no_show: "bg-zinc-200 text-zinc-700" };

export default function TablesPage() {
  const { biz, setBiz, features } = useOutletContext();
  const [date, setDate] = useState(ymd(new Date()));
  const [items, setItems] = useState(null);
  const load = () => api.get("/table-reservations", { params: { date } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const setStatus = async (id, status) => { await api.put(`/table-reservations/${id}`, { status }); toast.success("Updated"); load(); };
  const covers = (items || []).filter((i) => !["cancelled", "no_show"].includes(i.status)).reduce((s, i) => s + i.party_size, 0);

  return (
    <div className="space-y-8" data-testid="tables-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Table Reservations</h1>
          <p className="text-muted-foreground text-sm">Guests book from your hub; you manage covers here.</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="tables-date" />
          <div className="text-sm text-muted-foreground" data-testid="tables-covers">{covers} covers</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items === null ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <Armchair className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground text-sm">No reservations for this date.</p>
            </div>
          ) : items.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-md p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`table-res-${t.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-lg text-foreground">{t.time}</span>
                  <p className="text-foreground font-medium">{t.name} · {t.party_size} guests</p>
                  <Badge className={`${statusColor[t.status]} border-0 capitalize`}>{t.status.replace("_", " ")}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{[t.phone, t.email, t.source === "staff" ? "Added by staff" : "Via hub"].filter(Boolean).join(" · ")}</p>
                {t.notes && <p className="text-sm text-muted-foreground italic mt-1">“{t.notes}”</p>}
              </div>
              <Select value={t.status} onValueChange={(v) => setStatus(t.id, v)}>
                <SelectTrigger className="w-36" data-testid={`table-res-status-${t.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="space-y-6">
          <AddReservation date={date} onAdded={load} />
          <TableSettings biz={biz} setBiz={setBiz} />
        </div>
      </div>
      {features?.table_ordering && biz && <TableQRCodes slug={biz.slug} />}
    </div>
  );
}

function AddReservation({ date, onAdded }) {
  const [f, setF] = useState({ name: "", time: "19:00", party_size: 2, phone: "" });
  const add = async () => {
    try { await api.post("/table-reservations", { ...f, date }); toast.success("Reservation added"); setF({ ...f, name: "", phone: "" }); onAdded(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  return (
    <div className="bg-card border border-border rounded-md p-5 space-y-3" data-testid="add-table-res">
      <h3 className="font-serif text-lg text-foreground">Add a reservation</h3>
      <Input placeholder="Guest name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-res-name" />
      <div className="grid grid-cols-2 gap-3">
        <Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="add-res-time" />
        <Input type="number" min={1} value={f.party_size} onChange={(e) => setF({ ...f, party_size: e.target.value })} data-testid="add-res-party" />
      </div>
      <Input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="add-res-phone" />
      <Button onClick={add} className="w-full" data-testid="add-res-submit"><Plus className="w-4 h-4 mr-1" /> Add for {date}</Button>
    </div>
  );
}

function TableSettings({ biz, setBiz }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (biz && !s) { setS({ table_open: biz.table_open || "12:00", table_close: biz.table_close || "22:00", table_slot_minutes: biz.table_slot_minutes || 30, table_max_covers: biz.table_max_covers || 40, table_max_party: biz.table_max_party || 12 }); }
  }, [biz, s]);
  if (!s) return null;
  const save = async () => {
    try { const { data } = await api.put("/business", { ...s, table_slot_minutes: +s.table_slot_minutes, table_max_covers: +s.table_max_covers, table_max_party: +s.table_max_party }); setBiz(data); toast.success("Booking rules saved"); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Could not save"); }
  };
  const field = (k, label, type = "number") => (
    <div><Label className="text-xs">{label}</Label><Input type={type} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1" data-testid={`table-setting-${k}`} /></div>
  );
  return (
    <div className="bg-card border border-border rounded-md p-5 space-y-3" data-testid="table-settings">
      <h3 className="font-serif text-lg text-foreground">Booking rules</h3>
      <div className="grid grid-cols-2 gap-3">{field("table_open", "First seating", "time")}{field("table_close", "Last seating", "time")}</div>
      <div className="grid grid-cols-3 gap-3">{field("table_slot_minutes", "Slot (min)")}{field("table_max_covers", "Covers / slot")}{field("table_max_party", "Max party")}</div>
      <Button variant="outline" onClick={save} className="w-full" data-testid="table-settings-save"><Save className="w-4 h-4 mr-1" /> Save rules</Button>
    </div>
  );
}

function TableQRCodes({ slug }) {
  const [count, setCount] = useState(6);
  const n = Math.min(Math.max(parseInt(count, 10) || 0, 0), 60);
  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="table-qr-codes">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-lg text-foreground">Order-to-table QR codes</h3>
          <p className="text-xs text-muted-foreground">Print and place one on each table — diners scan to order from your menu.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Tables</Label>
          <Input type="number" min={1} max={60} value={count} onChange={(e) => setCount(e.target.value)} className="w-20" data-testid="table-qr-count" />
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="table-qr-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: n }, (_, i) => i + 1).map((t) => (
          <div key={t} className="border border-border rounded-md p-3 text-center" data-testid={`table-qr-${t}`}>
            <QRCodeCanvas value={`${window.location.origin}/h/${slug}?table=${t}`} size={110} level="M" includeMargin />
            <p className="font-serif text-foreground mt-1">Table {t}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
